import type { StaticFileDto } from 'BrokerTsLib/src/dtos'
import type { Document, DocumentLink } from 'BrokerTsLib/src/dtos'
import type { ApiLogDetail, ApiLogSimpleItem } from 'BrokerTsLib/src/dtos/Api/ApiLog'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { longStaleTime } from 'BrokerTsLib/src/hooks'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Product } from 'BrokerTsLib/src/enums'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'
import { useApp } from './useApp'
import { formatDateTime } from 'BrokerTsLib/src/Utils'

export const useGetAllDocuments = (paramsId: string | undefined | null, insurerOfferId: number, options?: { enabled?: boolean }) => {
  const url = useUrl(Apis.document)
  const { product } = useApp()

  return useQuery({
    queryKey: ['GetAllDocuments', paramsId, insurerOfferId, product],
    queryFn: async () => {
      const res = await apiClient.get<DocumentLink[]>(`${url}/GetAllDocuments`, { params: { paramsId: paramsId ?? '', insurerOfferId } })

      if (!Array.isArray(res.data))
        throw new Error('Invalid response data.')

      return res.data
    },
    retry: 3,
    retryDelay: (count) => count * 1000,
    ...options
  })
}

export const useGetOfferDocuments = (offerId: number, options?: GenericQueryOptions<Document[]>) => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['GetOfferDocuments', offerId],
    queryFn: () => apiClient.get<Document[]>(`${url}/GetOfferDocuments?offerId=${offerId}`),
    retry: false, ...options
  })
}

export const useGetApiLog = (apiLogId: number) => {
  const url = useUrl(Apis.document, Product.VehicleInsurance)

  return useQuery({
    queryKey: ['GetApiLog', apiLogId],
    queryFn: async () => {
      const res = await apiClient.get<ApiLogDetail>(`${url}/GetApiLog?apiLogId=${apiLogId}`)
      return res.data
    },
    retry: false
  })
}

export const useGetApiLogs = (paramsId: string, options?: GenericQueryOptions<ApiLogSimpleItem[]>) => {
  const url = useUrl(Apis.document, Product.VehicleInsurance)

  return useQuery({
    queryKey: ['GetApiLogs', paramsId],
    queryFn: () => apiClient.get<ApiLogSimpleItem[]>(`${url}/GetApiLogs?paramsId=${paramsId}`),
    retry: false,
    staleTime: longStaleTime,
    ...options
  })
}

export const useGetBrandLogo = (categoryId?: number | null, brand?: string | null, options?: GenericQueryOptions<string>) => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['GetBrandLogo', categoryId, brand],
    queryFn: () => apiClient.get<string>(`${url}/GetBrandLogoBase64?categoryId=${categoryId}&brand=${brand}`),
    staleTime: longStaleTime,
    refetchOnMount: false,
    retry: false,
    ...options
  })
}

export type VehicleIdentificationLinkPropsDto = {
  paramsId?: string
  spz?: string | null
  vin?: string | null
  vehicleId?: string
}


export const useGetStaticFiles = () => {
  const url = useUrl(Apis.document)

  return useQuery({
    queryKey: ['staticFiles'],
    queryFn: () => apiClient.get<StaticFileDto[]>(`${url}/GetStaticFiles`).then(res => res.data)
  })
}


const toStaticFileFormData = (dto: StaticFileDto): FormData => {
  const formData = new FormData()
  formData.append('id', String(dto.id))
  formData.append('type', String(dto.type))
  if (dto.name != null) formData.append('name', dto.name)
  if (dto.fileName != null) formData.append('fileName', dto.fileName)
  if (dto.insurerId != null) formData.append('insurerId', String(dto.insurerId))
  if (dto.code != null) formData.append('code', dto.code)
  if (dto.validFrom != null) formData.append('validFrom', formatDateTime(new Date(dto.validFrom)))
  if (dto.validTo != null) formData.append('validTo', formatDateTime(new Date(dto.validTo)))
  if (dto.file != null) formData.append('file', dto.file)
  return formData
}

export const useCreateStaticFile = () => {
  const url = useUrl(Apis.document)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: StaticFileDto) => apiClient.post<StaticFileDto>(`${url}/CreateStaticFile`, toStaticFileFormData(file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staticFiles'] })
    }
  })
}

export const useUpdateStaticFile = () => {
  const url = useUrl(Apis.document)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: StaticFileDto) => apiClient.put<StaticFileDto>(`${url}/UpdateStaticFile`, toStaticFileFormData(file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staticFiles'] })
    }
  })
}
